import { NavItem } from '@/deskUiSkeletons/Menu/NavItem'
import { ParentsMenu } from '@/deskUiSkeletons/Menu/ParentsMenu'
import { useDeskMenu } from '@/deskUiSkeletons/Menu/menuModel'
import { DeskStateBadge } from '@/deskUiSkeletons/Shared/DeskStateBadge'
import { legacyContractDataAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import type { DeskViewMode } from '@/msgdeskRuntime/deskNavigation'
import {
  handleBrandClick,
  handleEditClick,
  handleMenuItemOver,
  handleReplyClick,
  handleReplyEditClick,
  handleThisMessageOver,
} from './menuActions'
import { BackButton } from './components/BackButton'
import { FolderNavItem } from './components/FolderNavItem'
import { MenuActionButton } from './components/MenuActionButton'
import type { PortalMenuConfig, PortalMenuLabels } from './types'

// Shared portal-style menu (legacy e0-e43). Home + "this message" +
// back/action buttons are bespoke; the routing entries reuse <NavItem>.
// Behaviour handlers live in ./menuActions; the morphing action control and
// back button are their own components under ./components. Surface slots opt
// into this finished styled component through @/msgComponents/PortalMenu.
// All user-facing navigation vocabulary arrives from the consuming surface.

function Brand({ label }: { label: string }) {
  return (
    <li
      className="md-sidebar__home flex h-[34px] flex-[0_0_auto] cursor-pointer items-center rounded-[9px] py-0 pl-2.5 pr-3.5 text-[13px] font-medium leading-none text-[#5b6675] transition-colors duration-150 hover:bg-[#2563eb1a] hover:text-[#2563eb]"
      onClick={handleBrandClick}
      onMouseOver={(e) => handleMenuItemOver(e, true)}
      onMouseOut={(e) => handleMenuItemOver(e, false)}
    >
      <a href="/" className="flex items-center gap-[7px] text-base font-extrabold tracking-[-0.02em] text-[#2563eb] no-underline before:block before:h-[11px] before:w-[11px] before:rounded-[3px] before:bg-gradient-to-br before:from-[#2563eb] before:to-[#22d3ee]">
        {label}
      </a>
    </li>
  )
}

function AnswerButtonsBar({ canReply, canEdit, labels }: { canReply: boolean; canEdit: boolean; labels: PortalMenuLabels }) {
  return (
    <div className="md-sidebar__reply-container flex items-center px-0.5">
      <div className="md-sidebar__reply flex h-[34px] flex-col items-stretch" style={{ display: canReply || canEdit ? undefined : 'none' }}>
        <div className="md-sidebar__reply-top flex h-[34px] flex-row items-center gap-1.5">
          <span
            className="md-sidebar__reply-edit flex h-6 w-6 flex-[0_0_24px] cursor-pointer items-center justify-center rounded-[7px] bg-[#2563eb1f] text-[#2563eb] transition-colors duration-150 hover:bg-[#2563eb38]"
            style={{ display: canReply ? undefined : 'none' }}
            onClick={handleReplyEditClick}
          >
            <span aria-hidden="true" className="text-[13px] leading-none">✎</span>
          </span>
          <span
            className="flex flex-[0_0_auto] cursor-pointer items-center text-[13px] font-semibold text-[#2563eb]"
            style={{ display: canReply ? undefined : 'none' }}
            onClick={handleReplyClick}
          >
            {labels.reply}
          </span>
          {/* POSSESSOR-ONLY: opens the update-main editor (open-edit-message).
              This affordance was missing entirely — the /edit route was the
              only path and it raced the URL sync (audit 2026-07-19 D2). */}
          {canEdit ? (
            <span
              className="md-sidebar__main-edit flex flex-[0_0_auto] cursor-pointer items-center rounded-[7px] px-1.5 text-[13px] font-semibold text-[#5b6675] transition-colors duration-150 hover:bg-[#2563eb1a] hover:text-[#2563eb]"
              onClick={handleEditClick}
              title={labels.editThisMessageTitle}
            >
              {labels.edit}
            </span>
          ) : null}
        </div>
        <div className="md-sidebar__top-place hidden">
          <div className="md-sidebar__qr-top" />
        </div>
        <div className="md-sidebar__qr-place hidden" />
      </div>
    </div>
  )
}

function ThisMessageItem({ canReply, canEdit, labels }: { canReply: boolean; canEdit: boolean; labels: PortalMenuLabels }) {
  // Main message view only: shows the Reply control and is marked selected.
  return (
    <li
      className="md-sidebar__this-message md-sidebar__selected flex h-[34px] flex-[0_0_auto] cursor-pointer items-center overflow-visible rounded-[10px] bg-[#2563eb1a] py-0 pl-1.5 pr-1 text-[13px] font-medium leading-none text-white transition-colors duration-150"
      onMouseOver={(e) => handleThisMessageOver(e, true)}
      onMouseOut={(e) => handleThisMessageOver(e, false)}
    >
      <AnswerButtonsBar canReply={canReply} canEdit={canEdit} labels={labels} />
      <div className="md-sidebar__qr-main-place hidden" />
    </li>
  )
}

function HelpItem({ href, label }: { href: string; label: string }) {
  return (
    <li
      className="md-sidebar__nav-item md-sidebar__help ml-auto flex h-[34px] flex-[0_0_auto] cursor-pointer items-center whitespace-nowrap rounded-[9px] px-3 text-[13px] font-medium leading-none text-[#5b6675] transition-colors duration-150 hover:bg-[#2563eb1a] hover:text-[#2563eb]"
      onMouseOver={(e) => handleMenuItemOver(e, true)}
      onMouseOut={(e) => handleMenuItemOver(e, false)}
    >
      <a href={href} className="text-inherit no-underline">{label}</a>
    </li>
  )
}

export function PortalMenu({ isEditorOpen = false, config }: { isEditorOpen?: boolean; config: PortalMenuConfig }) {
  const menu = useDeskMenu()
  const access = menu.access
  const { labels } = config
  // Only the default message view ('msg') is "main". Every other view — the
  // Answers/Messages tabs ('subs') and Attachments/Bookmarks/Drafts/Basket —
  // is a tab, so it shows the back-to-main button.
  const isMainView = !isEditorOpen && menu.mode === 'msg'

  // Loading indicator: the bridge captures legacy's exact count into the
  // model's per-tab isLoading/loadingCount (elId === 0 = top-level tab load)
  // → the matching tab renders the count in its OWN label ("+N") and
  // animates; no extra element and no width change (NavItem pins its idle
  // width).
  function tabProps(tabMode: DeskViewMode) {
    const tab = menu.tabByMode[tabMode]
    return {
      mode: tabMode,
      isActive: tab.isActive,
      isLoading: tab.isLoading,
      loadingCount: tab.loadingCount,
    }
  }

  // LOCKED-SCREEN: a show/time-locked desk shows ONLY the locked template.
  // The menu is kept MOUNTED (sidebar.root is the legacy/facade address for
  // the whole menu and its inner qr/edit contracts) but hidden, so nothing
  // but the locked notice (which carries its own pin form) is visible.
  const locked = !access.showUnlocked

  return (
    // SKELETON: sidebar.root is the legacy/facade address for the whole menu.
    // Move this hook with the menu root; designers can freely change md-* CSS.
    <div
      {...legacyContractDataAttrs('sidebar.root')}
      className={`md-sidebar fixed left-2.5 top-2.5 z-[99999] isolate flex h-[46px] w-max min-w-[765px] max-w-[calc(100vw-20px)] items-center overflow-visible rounded-[14px] border border-[rgba(15,23,42,0.08)] bg-white/80 px-2 text-[#0f172a] shadow-[0_10px_30px_rgba(15,23,42,0.14),inset_0_1px_0_rgba(255,255,255,0.6)] backdrop-blur-[14px] font-[Arial,Tahoma,Verdana,sans-serif]${locked ? ' hidden' : ''}`}
      style={locked ? { display: 'none' } : undefined}
    >
      <div className="md-sidebar__inner relative flex w-full flex-1 items-center">
        <ul className="md-sidebar__list md-sidebar__list--root flex h-full flex-1 list-none items-center gap-1.5 bg-transparent p-0 m-0 whitespace-nowrap">
          <Brand label={labels.brand} />
          <ul className="md-sidebar__list md-sidebar__list--toggle flex h-full flex-1 list-none items-center gap-[3px] bg-transparent p-0 m-0 whitespace-nowrap">
            {/* main view → "this message" (Reply); any tab or edit → back button */}
            {isMainView
              ? <ThisMessageItem canReply={menu.can.reply} canEdit={menu.grant.capabilities.can_edit} labels={labels} />
              : <BackButton label={labels.backToMain} />}
            {/* super button: edit/new ("+") on main, save/config in editor, nothing on a tab.
                POSSESSION-MODEL: only the possessor (edit-unlocked) gets the
                edit/new tools — everyone else has the answers-only surface. */}
            {isMainView && access.editUnlocked ? (
              <MenuActionButton mode="main" canNewMsg={menu.can.createMessage} labels={labels} />
            ) : isEditorOpen ? (
              <MenuActionButton mode="edit" labels={labels} />
            ) : null}
            {/* Parents chain: legacy placed it between this-message and the tabs. */}
            <ParentsMenu
              as="li"
              labels={{ trigger: labels.parents, home: labels.parentsHome }}
              skin={{
                root: 'md-sidebar__nav-item--parents relative flex h-[34px] flex-[0_0_auto] list-none items-center',
                trigger: 'flex h-[34px] cursor-pointer items-center gap-1 whitespace-nowrap rounded-[9px] border-0 bg-transparent px-3 text-[13px] font-medium leading-none text-[#5b6675] transition-colors duration-150 hover:bg-[#2563eb1a] hover:text-[#2563eb]',
                triggerOpen: 'bg-[#2563eb1a] text-[#2563eb]',
                count: 'text-[11px] text-[rgba(0,0,0,0.4)]',
                list: 'absolute left-0 top-full z-[100000] flex min-w-[180px] max-w-[320px] flex-col gap-0.5 rounded-[10px] border border-[rgba(15,23,42,0.08)] bg-white/95 p-2 shadow-[0_10px_30px_rgba(15,23,42,0.14)] backdrop-blur-[14px]',
                link: 'overflow-hidden text-ellipsis whitespace-nowrap rounded-[7px] px-2 py-1 text-[13px] text-[#147fcb] no-underline hover:bg-[#eceef5] hover:underline',
                homeLink: 'mt-0.5 overflow-hidden text-ellipsis whitespace-nowrap rounded-[7px] border-t border-[rgba(15,23,42,0.08)] px-2 py-1 pt-1.5 text-[13px] text-[#5b6675] no-underline hover:bg-[#eceef5] hover:underline',
              }}
            />
            {menu.tabByMode.answers.hasItems ? <NavItem label={labels.answers} className="md-sidebar__nav-item md-sidebar__nav-item--answers" {...tabProps('answers')} /> : null}
            {/* §0.16 TWO-isMsg-SCOPES: an answer desk has no Messages branch —
                the tab is model-unavailable there and is not rendered (these
                NavItems carry no legacy mount contract). */}
            {menu.tabByMode.submsges.available && menu.tabByMode.submsges.hasItems ? <NavItem label={labels.messages} className="md-sidebar__nav-item" {...tabProps('submsges')} /> : null}
            {menu.tabByMode.attachments.hasItems ? <NavItem label={labels.attachments} className="md-sidebar__nav-item" {...tabProps('attachments')} /> : null}
            {/* Folder tabs: hover reveals the Answers/Msges section flyout.
                ANSWER-DESK RULE (§0.16, model-owned withSections): ALL of an
                answer's folders — bookmarks included — hold only answer-type
                subs, so they keep the plain tab there.
                FAVS-IDENTITY: Bookmarks shows for owner state or a logged
                visitor (their own bucket); never for anonymous viewers of an
                edit-locked desk. */}
            {menu.can.favs && menu.tabByMode.favs.hasItems ? <FolderNavItem label={labels.bookmarks} className="md-sidebar__nav-item" {...tabProps('favs')} withSections={menu.tabByMode.favs.withSections} showEmptySections={false} sectionLabels={[labels.folderAnswers, labels.folderMessages]} sectionCounts={menu.tabByMode.favs.sectionCounts} /> : null}
            {access.editUnlocked && menu.tabByMode.drafts.hasItems ? <FolderNavItem label={labels.drafts} className="md-sidebar__nav-item" {...tabProps('drafts')} withSections={menu.tabByMode.drafts.withSections} showEmptySections={false} sectionLabels={[labels.folderAnswers, labels.folderMessages]} sectionCounts={menu.tabByMode.drafts.sectionCounts} /> : null}
            {access.editUnlocked && menu.tabByMode.trash.hasItems ? <FolderNavItem label={labels.trash} className="md-sidebar__nav-item" {...tabProps('trash')} withSections={menu.tabByMode.trash.withSections} showEmptySections={false} sectionLabels={[labels.folderAnswers, labels.folderMessages]} sectionCounts={menu.tabByMode.trash.sectionCounts} /> : null}
            <HelpItem href={config.helpHref} label={labels.help} />
            {/* POSSESSION-MODEL: the menu's far right shows the MAIN MSG's
                own author-state (the main msg rules the menu); the logged
                identity moved to the page top-right (MsgScreen). */}
            <li className="flex h-full list-none items-center">
              <DeskStateBadge skin={{
                root: 'flex items-center whitespace-nowrap pl-2.5 ml-1 border-l border-[rgba(15,23,42,0.08)] text-[12px] font-medium',
                possessedOpen: 'text-[#0f8a4d]',
                possessedPin: 'text-[#0f8a4d]',
                locked: 'text-[#94a3b8]',
                label: 'cursor-pointer border-0 bg-transparent p-0 text-inherit underline decoration-dotted underline-offset-2 hover:text-[#2563eb]',
                form: 'ml-1.5 inline-flex items-center gap-1',
                input: 'h-[26px] w-[110px] rounded-[7px] border border-[#d9e1e8] bg-white px-2 text-[12px] text-[#17212b] outline-none focus:border-[#2563eb]',
                button: 'h-[26px] cursor-pointer rounded-[7px] px-2 text-[12px] text-[#147fcb] hover:bg-[#eceef5]',
              }} />
            </li>
          </ul>
        </ul>
      </div>
    </div>
  )
}
